/**
 * FortifyID plan catalog — single source of truth for marketing pricing,
 * staff-hub billing UI, Stripe product/price seeding, overage metering,
 * and CTAs that carry plan intent into signup.
 *
 * Prices are in AUD cents. Annual cadence is billed once a year at a
 * 10% discount versus 12 monthly payments (rounded to the nearest cent).
 */

export const CURRENCY = "aud" as const;

/** 14-day free trial. Only offered on the Growth plan. */
export const TRIAL_DAYS = 14 as const;

/** Maximum requests a workspace may create during a Growth trial. */
export const TRIAL_REQUEST_CAP = 10 as const;

/** Stripe metadata key used to look up our seeded products idempotently. */
export const STRIPE_METADATA_KEY = "fortifyid_plan" as const;
/** Stripe metadata key used to mark cadence on a price. */
export const STRIPE_CADENCE_METADATA_KEY = "fortifyid_cadence" as const;
/** Stripe metadata key used to mark the overage price. */
export const STRIPE_OVERAGE_METADATA_KEY = "fortifyid_overage" as const;

/** Per-verification overage charge for usage above the included monthly amount. AUD cents. */
export const OVERAGE_UNIT_AMOUNT_CENTS = 250 as const;

/** Annual discount versus 12× monthly (10%). */
export const ANNUAL_DISCOUNT = 0.1 as const;

export type PlanSlug = "starter" | "growth" | "scale";
export type Cadence = "monthly" | "annual";

export interface PlanDefinition {
  slug: PlanSlug;
  name: string;
  blurb: string;
  /** Monthly price in AUD cents. */
  monthlyAmountCents: number;
  /** Included verifications per month. Overage bills at OVERAGE_UNIT_AMOUNT_CENTS. */
  includedVerificationsPerMonth: number;
  /** Maximum staff seats. null = unlimited. */
  seatLimit: number | null;
  /** Whether this plan supports a custom verify domain. */
  customDomain: boolean;
  /** Whether this plan includes the drag-and-drop custom form builder. */
  customForms: boolean;
  /** Whether this plan supports custom branding (logo + colours). */
  customBranding: boolean;
  /** Highlighted as the recommended plan on the marketing pricing grid. */
  highlight: boolean;
  /** Whether this plan includes a free trial (TRIAL_DAYS, capped at TRIAL_REQUEST_CAP requests). */
  trial: boolean;
  /** Plain-text marketing bullets shown on the pricing card. */
  features: string[];
}

export const PLANS: readonly PlanDefinition[] = [
  {
    slug: "starter",
    name: "Starter",
    blurb: "For independent offices getting their first verified flow live.",
    monthlyAmountCents: 4000,
    includedVerificationsPerMonth: 20,
    seatLimit: 5,
    customDomain: false,
    customForms: false,
    customBranding: false,
    highlight: false,
    trial: false,
    features: [
      "20 verified requests / month included",
      "1 workspace, up to 5 staff seats",
      "Per-agency ConnectID",
      "Co-branded client surface",
      "Email support",
    ],
  },
  {
    slug: "growth",
    name: "Growth",
    blurb: "Multi-office groups with higher request volumes and audit needs.",
    monthlyAmountCents: 9500,
    includedVerificationsPerMonth: 50,
    seatLimit: null,
    customDomain: true,
    customForms: true,
    customBranding: true,
    highlight: true,
    trial: true,
    features: [
      "50 verified requests / month included",
      `${TRIAL_DAYS}-day free trial (up to ${TRIAL_REQUEST_CAP} requests)`,
      "Unlimited staff seats",
      "Custom branding (logo + colours)",
      "Custom domain on the client surface",
      "Custom form builder",
      "Priority support",
      "Quarterly audit export",
    ],
  },
  {
    slug: "scale",
    name: "Scale",
    blurb: "Franchise networks and enterprise property managers.",
    monthlyAmountCents: 18000,
    includedVerificationsPerMonth: 100,
    seatLimit: null,
    customDomain: true,
    customForms: true,
    customBranding: true,
    highlight: false,
    trial: false,
    features: [
      "100 verified requests / month included",
      "Multi-workspace administration",
      "Custom branding (logo + colours)",
      "Custom form builder",
      "SLA-backed uptime",
      "Dedicated CSM",
      "Custom DPA",
    ],
  },
] as const;

const PLANS_BY_SLUG = new Map(PLANS.map((p) => [p.slug, p]));

export function getPlan(slug: PlanSlug): PlanDefinition {
  const plan = PLANS_BY_SLUG.get(slug);
  if (!plan) throw new Error(`Unknown plan slug: ${slug}`);
  return plan;
}

export function isPlanSlug(value: unknown): value is PlanSlug {
  return typeof value === "string" && PLANS_BY_SLUG.has(value as PlanSlug);
}

export function isCadence(value: unknown): value is Cadence {
  return value === "monthly" || value === "annual";
}

/** Annual price in AUD cents = monthly × 12 × (1 - 10%), rounded to nearest cent. */
export function annualAmountCents(plan: PlanDefinition): number {
  return Math.round(plan.monthlyAmountCents * 12 * (1 - ANNUAL_DISCOUNT));
}

/** Effective monthly price when billed annually, for display purposes. */
export function annualEffectiveMonthlyCents(plan: PlanDefinition): number {
  return Math.round(annualAmountCents(plan) / 12);
}

export function cadenceAmountCents(plan: PlanDefinition, cadence: Cadence): number {
  return cadence === "monthly" ? plan.monthlyAmountCents : annualAmountCents(plan);
}

export function formatAud(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/** Friendly overage line shown on cards + FAQ. */
export const OVERAGE_LABEL = `${formatAud(OVERAGE_UNIT_AMOUNT_CENTS)} per additional verification`;
